import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import {
  Container,
  Grid,
  Divider,
  LinearProgress,
  Button,
} from "@material-ui/core";

import FilterCard from "components/_refactor/filter-card";
import StructureFields from "components/_refactor/fields/structure";
import HeaderClientes from "components/_refactor/headers/clients";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import TableProductsList from "../minhas-vitrines/components/table-products-list";
import TableNovaVitrineVirtual from "./components/table-nova-vitrine";
import Api from "lib/api/httpRequestHelper";
import useLogs from "hooks/logs";
import { IsMobile, IsMobile900 } from "utils/is-mobile";
import InputText from "components/_refactor/text/input";
import UtilsFncs from "utils/UtilsFncs";

const useCustomStyles = makeStyles({
  buttonStyle: {
    border: "none",
    background: "transparent",
    color: "grey",
    "&:hover": {
      color: "#DF767D",
      textDecoration: "underline",
    },
  },
});

const createProductObject = (item, isDisabled = false) => ({
  Codigo: item?.Codigo,
  Ean: item?.Ean,
  EstoqueDisponivel: item?.EstoqueDisponivel,
  Foto: item?.Foto,
  FotoPrincipal: item?.FotoPrincipal,
  ListaProduto: item?.ListaProduto,
  Nome: item?.Nome?.toUpperCase(),
  QuantidadeEstoque: item?.QuantidadeEstoque,
  QuantidadeEstoqueFormat: item?.QuantidadeEstoqueFormat,
  Disabled: isDisabled,
});

export default function CriarVitrineVirtual() {
  const classes = useCustomStyles();
  const snackbar = useSnackbar();
  const history = useHistory();

  const { setLocalStorageApiProblem, setLocalStorageActions } = useLogs();

  const [showcaseName, setShowcaseName] = useState("");
  const [active, setActive] = useState(true);

  const [initialDate, setInitialDate] = useState("");
  const [finalDate, setFinalDate] = useState("");

  const [productFilter, setProductFilter] = useState("");
  const [productsList, setProductsList] = useState([]);
  const [openProductDialog, setOpenProductDialog] = useState(false);

  const [showcaseItemsList, setShowcaseItemsList] = useState([]);
  const [creatingShowcase, setCreatingShowcase] = useState(false);

  const [searchingProducts, setSearchingProducts] = useState(false);

  const [structureFilters, setStructureFilters] = useState({});
  const [structureLoading, setStructureLoading] = useState([]);

  const [clearFilters, setClearFilters] = useState(false);

  const [totalRegisters, setTotalRegisters] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);

  const [isExclusive, setIsExclusive] = useState(false);
  const [seller, setSeller] = useState("");
  const [sellerList, setSellerList] = useState([]);
  const [searchingSeller, setSearchingSeller] = useState(false);

  const isMobile900 = IsMobile900();
  const isMobile = IsMobile();

  const itemXS = isMobile ? 12 : 6;

  const handleGetProducts = async ({ page = 1, showcaseList = [] }) => {
    setSearchingProducts(true);

    setLocalStorageActions({
      nomeTela: "Criar Vitrine Virtual",
      idMenu: "160",
      acao: "Clicou para Pesquisar",
    });

    try {
      const data = await Api.get(
        "product-simple-list",
        "product",
        `?page=${
          page - 1
        }&pageSize=10&order=D&filter=${productFilter}&idMarca=${structureFilters?.idMarca?.toString()}&idPessoaLoja=${structureFilters?.idLojaUnica?.toString()}`,
        true,
      );

      if (!data?.Result?.ListaProdutos?.length) {
        setProductsList([]);
        snackbar.showMessage(
          "Nenhum produto encontrado com os filtros selecionados.",
          { severity: "info" },
        );
        return;
      }

      const { ListaProdutos } = data.Result;
      const hasExistingItems =
        showcaseList?.length || showcaseItemsList?.length;

      if (hasExistingItems) {
        const verifyPreviousSelected = ListaProdutos.filter((newData) =>
          showcaseItemsList?.length
            ? showcaseItemsList.some(
                (selected) => selected?.Codigo === newData?.Codigo,
              )
            : showcaseList.some(
                (selected) => selected?.Codigo === newData?.Codigo,
              ),
        );

        const previousItemList = verifyPreviousSelected.map((item) =>
          createProductObject(item, true),
        );
        const compareItems = ListaProdutos.filter(
          (cv) => !verifyPreviousSelected.find((e) => e.Codigo === cv.Codigo),
        );
        const newList = compareItems.map((item) => createProductObject(item));

        setProductsList([...previousItemList, ...newList]);
      } else {
        const newList = ListaProdutos.map((item) => createProductObject(item));
        setProductsList(newList);
      }

      setTotalRegisters(data.Result.TotalRegistros);
      setOpenProductDialog(true);
    } catch (error) {
      console.error(error);
      snackbar.showMessage(error, { severity: "error" });
      setLocalStorageApiProblem({
        idMenuAccess: "160",
        screen: "Criar Vitrine Virtual",
        problem: `Erro na chamada do endpoint product-simple-list - ${error}`,
      });
    } finally {
      setCurrentPage(page);
      setSearchingProducts(false);
    }
  };

  const handleCreateShowcase = async () => {
    setCreatingShowcase(true);
    const idList = showcaseItemsList.map((product) => product?.Codigo);

    try {
      const resp = await Api.post(
        "create-virtual-showcase",
        "showcase",
        {
          nome: showcaseName,
          idPessoaLoja: structureFilters?.idLojaUnica?.toString(),
          dataVigenciaInicio: UtilsFncs.ConvertDateFormat(
            initialDate,
            "-",
            "-",
          ),
          dataVigenciaFim: UtilsFncs.ConvertDateFormat(finalDate, "-", "-"),
          idsProduto: idList.join(","),
          ativo: active.toString(),
          linkPublico: !isExclusive,
          idVendedorLoja: seller?.IdLojaVendedor,
        },
        true,
      );

      if (resp?.Success) {
        const url = `/admin?idLoja=${structureFilters?.idLojaUnica}`;
        snackbar.showMessage(resp?.Result?.MessageResponse, {
          severity: "success",
        });
        setTimeout(() => history.push(url), 500);
      }
    } catch (error) {
      console.error(error);
      snackbar.showMessage(error, { severity: "error" });
      setLocalStorageApiProblem({
        idMenuAccess: "160",
        screen: "Criar Vitrine Virtual",
        problem: `Erro na chamada do endpoint create-virtual-showcase - ${error}`,
      });
    } finally {
      setCreatingShowcase(false);
    }
  };

  const handleClearFilters = () => {
    setStructureLoading(true);
    setClearFilters(true);
    setProductsList([]);
    setShowcaseItemsList([]);
    setProductFilter("");
    setShowcaseName("");
    setStructureFilters((prevState) => {
      return { ...prevState, idLojaUnica: "" };
    });
  };

  useEffect(() => {
    if (clearFilters && showcaseName === "") {
      setClearFilters(false);
      setStructureLoading(false);
    }
  }, [clearFilters, showcaseName]);

  const renderLoadingState = (message) => (
    <div>
      <span>{message}</span>
      <LinearProgress />
    </div>
  );

  return (
    <>
      <HeaderClientes section="Criar Vitrine Virtual" subsection="Vendas" />
      <Container maxWidth={false} style={{ marginTop: "-4.5rem" }}>
        <FilterCard
          handleSearch={handleGetProducts}
          disabled={
            searchingProducts || creatingShowcase || structureLoading.length > 0
          }
          searching={searchingProducts}
          handleClearFilters={handleClearFilters}
        >
          <StructureFields
            clearFilters={clearFilters}
            showSingleStore={true}
            onStructureFieldsChange={setStructureFilters}
            disableAll={creatingShowcase || structureLoading?.length !== 0}
            onStructureFieldsLoad={setStructureLoading}
            requiredFields={["storeSingle"]}
            fieldsOrder={["storeSingle"]}
            hideFields={["channel", "representative", "economicGroup", "store"]}
            removeBrandAll={true}
          />

          <Grid item xs={12}>
            <Divider />
          </Grid>

          <Grid item xs={itemXS}>
            <InputText
              label="Produto"
              value={productFilter}
              onChange={(e) => {
                setProductFilter(e.target.value.slice(0, 100));
              }}
              disable={searchingProducts || creatingShowcase}
            />
          </Grid>
          <Grid
            item
            xs={itemXS}
            style={{ display: "flex", justifyContent: "center" }}
            hidden={
              openProductDialog ||
              productsList?.length === 0 ||
              creatingShowcase
            }
          >
            <Button
              className={classes.buttonStyle}
              style={
                isMobile || isMobile900
                  ? {
                      width: "100%",
                      marginBottom: "0.5rem",
                      marginTop: "0.75rem",
                    }
                  : { marginBottom: "0rem" }
              }
              onClick={() => setOpenProductDialog(true)}
            >
              Ver Produtos
            </Button>
          </Grid>
        </FilterCard>

        {searchingProducts &&
          !openProductDialog &&
          renderLoadingState("Buscando produtos...")}

        {creatingShowcase && renderLoadingState("Criando vitrine virtual...")}

        {productsList?.length !== 0 && openProductDialog && (
          <TableProductsList
            dataTable={productsList}
            search={handleGetProducts}
            currentPage={currentPage}
            totalRegistros={totalRegisters}
            setShowcaseItemsList={setShowcaseItemsList}
            showcaseItemsList={showcaseItemsList}
            setProductsList={setProductsList}
            searchingProducts={searchingProducts}
            open={openProductDialog}
            onClose={() => {
              setOpenProductDialog(false);
            }}
          />
        )}

        {showcaseItemsList?.length !== 0 && (
          <TableNovaVitrineVirtual
            handleCreateShowcase={handleCreateShowcase}
            setShowcaseItemsList={setShowcaseItemsList}
            creatingShowcase={creatingShowcase}
            setProductsList={setProductsList}
            setShowcaseName={setShowcaseName}
            dataTable={showcaseItemsList}
            name={showcaseName}
            active={active}
            setActive={setActive}
            initialDate={initialDate}
            setInitialDate={setInitialDate}
            finalDate={finalDate}
            setFinalDate={setFinalDate}
            isExclusive={isExclusive}
            setIsExclusive={setIsExclusive}
            seller={seller}
            setSeller={setSeller}
            sellerList={sellerList}
            setSellerList={setSellerList}
            searchingSeller={searchingSeller}
            setSearchingSeller={setSearchingSeller}
            idPessoaLoja={structureFilters?.idLojaUnica?.toString() || ""}
          />
        )}
      </Container>
    </>
  );
}
